import { Link, useNavigate } from 'react-router-dom'
import { type OrderDTO, type PatchOrderRequest } from '@wf/shared'
import { ApiError } from '../api/client'
import { ESTIMATE_LABEL } from './estimateStatus'
import { useDeleteOrder, usePatchOrder } from './useOrders'

/**
 * Auftrags-Aktionen am Detail: Kostenvoranschlag, Rechnung, Bezahlt markieren/zurücknehmen, Archivieren,
 * Löschen. „Kostenvoranschlag" und „Rechnung" öffnen jeweils eine wiederholbare Vorschau (/print/estimate
 * bzw. /print/invoice) — dort liegen die eigentlichen Aktionen (KV versenden / Rechnung festschreiben /
 * drucken). Bezahlt/Archivieren laufen über den OCC-Patch (version Pflicht); `skipped[]` aus der Antwort
 * wird ehrlich angezeigt (z. B. wenn ein Feld nach Rechnungsstellung GoBD-gesperrt ist). Abrechnungs-
 * Aktionen brauchen das Rechnungs-Recht, Archivieren das Verwaltungsrecht; der Server erzwingt beides.
 */
export function OrderEdit({
  order,
  canManage,
  canRechnung,
  canDelete,
}: {
  order: OrderDTO
  canManage: boolean
  canRechnung: boolean
  canDelete: boolean
}) {
  const navigate = useNavigate()
  const patch = usePatchOrder(order.id)
  const remove = useDeleteOrder(order.id)

  function runDelete(): void {
    if (!window.confirm(`Auftrag ${order.nr ?? order.id} endgültig löschen?`)) return
    remove.mutate(undefined, { onSuccess: () => navigate('/orders') })
  }

  const paid = order.paidAt != null
  const archived = order.archived === 1
  const skipped = patch.data?.skipped ?? []

  const apply = (body: Omit<PatchOrderRequest, 'version'>) => patch.mutate({ version: order.version, ...body })

  return (
    <section className="wf-orderedit">
      {canRechnung ? (
        <div className="wf-estimate">
          <span className="wf-label wf-sectiontitle">Kostenvoranschlag</span>
          <div className="wf-estimate__row">
            <span className={`wf-estimate__state wf-estimate__state--${order.estimateStatus}`}>
              {ESTIMATE_LABEL[order.estimateStatus] ?? order.estimateStatus}
            </span>
          </div>
        </div>
      ) : null}

      <div className="wf-orderedit__actions">
        {canRechnung ? (
          <>
            <Link to={`/print/estimate/${order.id}`} className="wf-btn wf-btn--ghost">
              Kostenvoranschlag
            </Link>
            <Link to={`/print/invoice/${order.id}`} className="wf-btn wf-btn--ghost">
              Rechnung
            </Link>
            {order.invNo ? <span className="wf-orderedit__invno">Rechnung {order.invNo}</span> : null}
          </>
        ) : null}
        {canRechnung ? (
          paid ? (
            <button type="button" className="wf-btn wf-btn--ghost" disabled={patch.isPending} onClick={() => apply({ paid: false })}>
              Bezahlt zurücknehmen
            </button>
          ) : (
            <button type="button" className="wf-btn" disabled={patch.isPending} onClick={() => apply({ paid: true, paidMethod: 'bar' })}>
              Als bezahlt markieren
            </button>
          )
        ) : null}
        {canManage ? (
          archived ? (
            <button type="button" className="wf-btn wf-btn--ghost" disabled={patch.isPending} onClick={() => apply({ archived: false })}>
              Zurückholen
            </button>
          ) : (
            <button type="button" className="wf-btn wf-btn--ghost" disabled={patch.isPending} onClick={() => apply({ archived: true })}>
              Archivieren
            </button>
          )
        ) : null}
        {canDelete ? (
          <button type="button" className="wf-btn wf-btn--danger" disabled={remove.isPending} onClick={runDelete}>
            Auftrag löschen
          </button>
        ) : null}
      </div>

      {skipped.length > 0 ? (
        <p className="warn" role="status">
          Nicht übernommen: {skipped.join(', ')} — Recht fehlt oder nach Rechnung/Freigabe gesperrt.
        </p>
      ) : null}
      {patch.error instanceof ApiError ? (
        <p className="error" role="alert">
          {patch.error.message}
        </p>
      ) : null}
      {remove.error instanceof ApiError ? (
        <p className="error" role="alert">
          Löschen fehlgeschlagen: {remove.error.message}
        </p>
      ) : null}
    </section>
  )
}
